import { types as sdkTypes } from './sdkLoader';

const { Money } = sdkTypes;

// A seller sale lowers the listing's real price; the server keeps the original price and sale
// details in the listing's metadata (server/seller-sales.js), which sellers can't edit. These
// read that for the strikethrough price and "Sale · ends" badge.

/**
 * The listing's running sale, or null: { originalPrice (Money), percentOff, endsAt (Date) }.
 * A sale whose end time has passed isn't shown, even before the hourly job puts the price back.
 */
export const getListingSale = (listing, now = Date.now()) => {
  const metadata = listing?.attributes?.metadata;
  const sale = metadata?.onSale === true ? metadata.sale : null;
  const original = sale?.originalPrice;
  const endsAt = sale?.endsAt ? new Date(sale.endsAt) : null;
  if (!original?.amount || !original.currency || !endsAt || endsAt.getTime() <= now) {
    return null;
  }
  return {
    originalPrice: new Money(original.amount, original.currency),
    percentOff: sale.percentOff,
    endsAt,
  };
};

const PRICE_RAISE_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Whether a listing has been up for 7 days without a price raise, which it needs before it can go
 * on sale (server/seller-sales.js checks this again), so a sale can't show an inflated original
 * price.
 */
export const hasStablePriceForSale = (listing, now = Date.now()) => {
  const createdAt = listing?.attributes?.createdAt;
  const raisedAt = listing?.attributes?.metadata?.priceRaisedAt;
  const isOldEnough = createdAt && now - new Date(createdAt).getTime() >= PRICE_RAISE_COOLDOWN_MS;
  const raisedRecently = raisedAt && now - new Date(raisedAt).getTime() < PRICE_RAISE_COOLDOWN_MS;
  return Boolean(isOldEnough) && !raisedRecently;
};

/** The shop's running sale from the seller's profile, or null: { percentOff, endsAt, scope }. */
export const getShopSale = (user, now = Date.now()) => {
  const shopSale = user?.attributes?.profile?.metadata?.shopSale;
  const endsAt = shopSale?.endsAt ? new Date(shopSale.endsAt) : null;
  if (!endsAt || shopSale.endedAt || endsAt.getTime() <= now) {
    return null;
  }
  return { percentOff: shopSale.percentOff, endsAt, scope: shopSale.scope };
};
